import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { makeAuthenticatedRequest, handleApiError } from '../utils/api';
import './AdminUsersManager.css';

interface AdminUser {
  id: number;
  username: string;
  email: string;
  role: string;
  isBanned: boolean;
  banReason?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  characterCount: number;
  totalBattles: number;
  totalPlayTime: string;
  status: string;
}

interface UserStats {
  totalUsers: number;
  totalPages: number;
  currentPage: number;
  roleBreakdown: { [role: string]: number };
}

interface RoleUpdateRequest {
  newRole: string;
  reason?: string;
}

interface BanUserRequest {
  reason: string;
}

const AdminUsersManager: React.FC = () => {
  const { handleAuthError } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form data
  const [roleFormData, setRoleFormData] = useState<RoleUpdateRequest>({
    newRole: 'Player',
    reason: ''
  });
  const [banFormData, setBanFormData] = useState<BanUserRequest>({
    reason: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder
      });

      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }
      if (filterRole) {
        queryParams.append('role', filterRole);
      }
      if (filterStatus) {
        queryParams.append('status', filterStatus);
      }

      const response = await makeAuthenticatedRequest(`/admin/users?${queryParams}`);

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setUserStats({
          totalUsers: data.totalUsers,
          totalPages: data.totalPages,
          currentPage: data.currentPage,
          roleBreakdown: data.roleBreakdown
        });
      } else {
        setError('Failed to load users');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error loading users');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, filterRole, filterStatus, sortBy, sortOrder, handleAuthError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRoleUpdate = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/users/${selectedUser.id}/role`, {
        method: 'PUT',
        body: JSON.stringify(roleFormData)
      });

      if (response.ok) {
        setShowRoleModal(false);
        setSelectedUser(null);
        setRoleFormData({ newRole: 'Player', reason: '' });
        await fetchUsers();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update user role');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error updating user role');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBanUser = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/users/${selectedUser.id}/ban`, {
        method: 'POST',
        body: JSON.stringify(banFormData)
      });

      if (response.ok) {
        setShowBanModal(false);
        setSelectedUser(null);
        setBanFormData({ reason: '' });
        await fetchUsers();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to ban user');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error banning user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnbanUser = async (userId: number) => {
    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/users/${userId}/unban`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchUsers();
        setError(null);
      } else {
        setError('Failed to unban user');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error unbanning user');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatPlayTime = (timeString: string): string => {
    try {
      const match = timeString.match(/(\d+):(\d+):(\d+)/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        return `${hours}h ${minutes}m`;
      }
      return timeString;
    } catch {
      return 'Unknown';
    }
  };

  const getRoleBadgeClass = (role: string): string => {
    switch (role.toLowerCase()) {
      case 'admin': return 'role-admin';
      case 'moderator': return 'role-moderator';
      case 'player': return 'role-player';
      case 'banned': return 'role-banned';
      default: return 'role-unknown';
    }
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'online': return 'status-online';
      case 'offline': return 'status-offline';
      case 'banned': return 'status-banned';
      case 'inactive': return 'status-inactive';
      default: return 'status-unknown';
    }
  };

  const openRoleModal = (user: AdminUser) => {
    setSelectedUser(user);
    setRoleFormData({ newRole: user.role, reason: '' });
    setShowRoleModal(true);
  };

  const openBanModal = (user: AdminUser) => {
    setSelectedUser(user);
    setBanFormData({ reason: '' });
    setShowBanModal(true);
  };

  const openUserDetails = (user: AdminUser) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const openDeleteModal = (user: AdminUser) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setIsLoading(true);
      const response = await makeAuthenticatedRequest(`/admin/users/${selectedUser.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedUser(null);
        await fetchUsers();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to delete user');
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.isAuthError) {
        handleAuthError();
        return;
      }
      setError('Error deleting user');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="users-manager">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-manager">
      <div className="users-header">
        <div className="users-title">
          <h2>👥 User Management</h2>
          <p>Manage user accounts, roles, and permissions</p>
        </div>
        <div className="user-stats-summary">
          {userStats && (
            <>
              <div className="stat-item">
                <span className="stat-label">Total Users:</span>
                <span className="stat-value">{userStats.totalUsers}</span>
              </div>
              <div className="role-breakdown">
                {userStats.roleBreakdown && Object.entries(userStats.roleBreakdown).map(([role, count]) => (
                  <div key={role} className={`role-stat ${getRoleBadgeClass(role)}`}>
                    {role}: {count}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="users-filters">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search users by username or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={fetchUsers} className="search-button">🔍 Search</button>
        </div>

        <div className="filter-group">
          <label>Role:</label>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
            <option value="">All Roles</option>
            <option value="Admin">Admin</option>
            <option value="Moderator">Moderator</option>
            <option value="Player">Player</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Banned">Banned</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="CreatedAt">Join Date</option>
            <option value="LastLoginAt">Last Login</option>
            <option value="Username">Username</option>
            <option value="Role">Role</option>
            <option value="CharacterCount">Characters</option>
            <option value="TotalBattles">Battles</option>
          </select>
          <button 
            className={`sort-order ${sortOrder === 'desc' ? 'desc' : 'asc'}`}
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button className="refresh-button" onClick={fetchUsers}>
          🔄 Refresh
        </button>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Role</th>
              <th>Status</th>
              <th>Activity</th>
              <th>Stats</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className="user-row">
                <td className="user-info">
                  <div className="user-identity">
                    <div className="username">{user.username}</div>
                    <div className="email">{user.email}</div>
                    <div className="user-id">ID: {user.id}</div>
                  </div>
                </td>
                <td>
                  <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                    {user.role}
                  </span>
                  {user.isBanned && (
                    <div className="ban-indicator">
                      🚫 Banned
                      {user.banReason && <div className="ban-reason">Reason: {user.banReason}</div>}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(user.status)}`}>
                    {user.status}
                  </span>
                </td>
                <td className="user-activity">
                  <div>Joined: {formatDateTime(user.createdAt)}</div>
                  {user.lastLoginAt && (
                    <div>Last Login: {formatDateTime(user.lastLoginAt)}</div>
                  )}
                  <div className="play-time">Play Time: {formatPlayTime(user.totalPlayTime)}</div>
                </td>
                <td className="user-stats">
                  <div>📊 {user.characterCount} Characters</div>
                  <div>⚔️ {user.totalBattles} Battles</div>
                </td>
                <td className="user-actions">
                  <button 
                    className="view-button"
                    onClick={() => openUserDetails(user)}
                  >
                    👁️ View
                  </button>
                  <button
                    className="role-button"
                    onClick={() => openRoleModal(user)}
                  >
                    🔧 Role
                  </button>
                  {user.isBanned ? (
                    <button
                      className="unban-button"
                      onClick={() => handleUnbanUser(user.id)}
                    >
                      ✅ Unban
                    </button>
                  ) : (
                    <button
                      className="ban-button"
                      onClick={() => openBanModal(user)}
                    >
                      🚫 Ban
                    </button>
                  )}
                  <button
                    className="delete-button"
                    onClick={() => openDeleteModal(user)}
                    title="Permanently delete this user"
                  >
                    🗑️ Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="empty-state">
            <h3>No users found</h3>
            <p>Try adjusting your search criteria or filters.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {userStats && userStats.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="page-button"
          >
            ← Previous
          </button>
          <span className="page-info">
            Page {currentPage} of {userStats.totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(Math.min(userStats.totalPages, currentPage + 1))}
            disabled={currentPage === userStats.totalPages}
            className="page-button"
          >
            Next →
          </button>
        </div>
      )}

      {/* Role Update Modal */}
      {showRoleModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔧 Update User Role</h3>
              <button onClick={() => setShowRoleModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-context">
                <strong>User:</strong> {selectedUser.username} ({selectedUser.email})
                <br />
                <strong>Current Role:</strong> <span className={`role-badge ${getRoleBadgeClass(selectedUser.role)}`}>{selectedUser.role}</span>
              </div>
              
              <div className="form-group">
                <label>New Role:</label>
                <select 
                  value={roleFormData.newRole}
                  onChange={(e) => setRoleFormData({...roleFormData, newRole: e.target.value})}
                >
                  <option value="Player">Player</option>
                  <option value="Moderator">Moderator</option>
                  <option value="Admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Reason (Optional):</label>
                <textarea 
                  value={roleFormData.reason}
                  onChange={(e) => setRoleFormData({...roleFormData, reason: e.target.value})}
                  placeholder="Enter reason for role change..."
                  rows={3}
                />
              </div>

              <div className="role-warning">
                ⚠️ <strong>Warning:</strong> Changing user roles affects their access permissions immediately.
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowRoleModal(false)}
              >
                Cancel
              </button>
              <button 
                className="update-button"
                onClick={handleRoleUpdate}
                disabled={roleFormData.newRole === selectedUser.role}
              >
                🔧 Update Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ban User Modal */}
      {showBanModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowBanModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🚫 Ban User</h3>
              <button onClick={() => setShowBanModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-context">
                <strong>User:</strong> {selectedUser.username} ({selectedUser.email})
                <br />
                <strong>Current Status:</strong> <span className={`status-badge ${getStatusBadgeClass(selectedUser.status)}`}>{selectedUser.status}</span>
              </div>
              
              <div className="form-group">
                <label>Ban Reason *</label>
                <textarea 
                  value={banFormData.reason}
                  onChange={(e) => setBanFormData({...banFormData, reason: e.target.value})}
                  placeholder="Enter detailed reason for banning this user..."
                  rows={4}
                  required
                />
              </div>

              <div className="ban-warning">
                ⚠️ <strong>Warning:</strong> Banned users will lose access to their account immediately. This action can be reversed later.
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowBanModal(false)}
              >
                Cancel
              </button>
              <button 
                className="ban-confirm-button"
                onClick={handleBanUser}
                disabled={!banFormData.reason.trim()}
              >
                🚫 Ban User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>👤 User Details</h3>
              <button onClick={() => setShowUserDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="detail-section">
                  <h4>Account Information</h4>
                  <div className="detail-grid">
                    <div><strong>User ID:</strong> {selectedUser.id}</div>
                    <div><strong>Username:</strong> {selectedUser.username}</div>
                    <div><strong>Email:</strong> {selectedUser.email}</div>
                    <div><strong>Role:</strong> <span className={`role-badge ${getRoleBadgeClass(selectedUser.role)}`}>{selectedUser.role}</span></div>
                    <div><strong>Status:</strong> <span className={`status-badge ${getStatusBadgeClass(selectedUser.status)}`}>{selectedUser.status}</span></div>
                    <div><strong>Banned:</strong> {selectedUser.isBanned ? '❌ Yes' : '✅ No'}</div>
                  </div>
                  {selectedUser.isBanned && selectedUser.banReason && (
                    <div className="ban-details">
                      <strong>Ban Reason:</strong>
                      <p>{selectedUser.banReason}</p>
                    </div>
                  )}
                </div>

                <div className="detail-section">
                  <h4>Activity & Stats</h4>
                  <div className="detail-grid">
                    <div><strong>Account Created:</strong> {formatDateTime(selectedUser.createdAt)}</div>
                    <div><strong>Last Updated:</strong> {formatDateTime(selectedUser.updatedAt)}</div>
                    {selectedUser.lastLoginAt && (
                      <div><strong>Last Login:</strong> {formatDateTime(selectedUser.lastLoginAt)}</div>
                    )}
                    <div><strong>Total Play Time:</strong> {formatPlayTime(selectedUser.totalPlayTime)}</div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Game Statistics</h4>
                  <div className="stats-display">
                    <div className="stat-item">
                      <span className="stat-icon">📊</span>
                      <span>Characters Created: {selectedUser.characterCount}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-icon">⚔️</span>
                      <span>Total Battles: {selectedUser.totalBattles}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="close-button"
                onClick={() => setShowUserDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🗑️ Delete User</h3>
              <button onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-context">
                <strong>User:</strong> {selectedUser.username} ({selectedUser.email})
                <br />
                <strong>Characters:</strong> {selectedUser.characterCount}
              </div>

              <div className="delete-warning">
                <div className="warning-header">⚠️ <strong>PERMANENT ACTION</strong></div>
                <p>You are about to permanently delete this user and ALL associated data:</p>
                <ul>
                  <li>User account and profile</li>
                  <li>All characters ({selectedUser.characterCount})</li>
                  <li>All battles and battle history</li>
                  <li>All crafted items and inventory</li>
                  <li>All progress and achievements</li>
                </ul>
                <p><strong>This action CANNOT be undone!</strong></p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={handleDeleteUser}
              >
                🗑️ Permanently Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersManager;